// components/ResponsiveImage.tsx

// Define the available size variants and their actual pixel widths
const SIZE_MAP: Record<string, number> = {
	small: 480,
	medium: 960,
	large: 1440,
};

type SizeName = keyof typeof SIZE_MAP;
type Format = "avif" | "webp" | "jpeg" | "png";

type ResponsiveImageProps = {
	/** Base image name without size or extension (e.g., "writers") */
	baseName: string;

	/** Custom sizes to use, defaults to ["small", "medium", "large"] */
	sizes?: SizeName[];

	/** Formats by preference (first is most preferred), default: avif > webp > jpeg */
	formats?: Format[];

	/** Custom base URL (if using custom domain) */
	baseUrl?: string;

	/** <img> alt text */
	alt?: string;

	/** Optional sizes attribute for responsive behavior */
	sizesAttr?: string;

	/** <img> class name */
	className?: string;

	/** Optional loading strategy */
	loading?: "eager" | "lazy";

	/** Optional decoding strategy */
	decoding?: "sync" | "async" | "auto";

	/** Optional class name for the <picture> element */
	pictureClassName?: string;
};

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
	baseName,
	sizes = ["small", "medium", "large"],
	formats = ["avif", "webp", "jpeg", "png"],
	baseUrl = "https://images.coffitivity.com",
	alt = "",
	sizesAttr = "(max-width: 1440px) 100vw, 1440px",
	className = "",
	loading = "lazy",
	decoding = "async",
	pictureClassName = "",
}) => {
	// Build a srcset string based on available sizes and format
	const buildSrcSet = (format: Format) =>
		sizes
			.map(
				(size) => `${baseUrl}/${baseName}-${size}.${format} ${SIZE_MAP[size]}w`,
			)
			.join(", ");

	const fallbackFormat = formats[formats.length - 1];
	const fallbackSize = "medium"; // default fallback size for <img src>
	const fallbackSrc = `${baseUrl}/${baseName}-${fallbackSize}.${fallbackFormat}`;
	const fallbackSrcSet = buildSrcSet(fallbackFormat);

	return (
		<picture className={pictureClassName}>
			{/* Source sets for modern formats (except fallback) */}
			{formats.slice(0, -1).map((format) => (
				<source
					key={format}
					type={`image/${format}`}
					srcSet={buildSrcSet(format)}
					sizes={sizesAttr}
				/>
			))}

			{/* Fallback <img> */}
			<img
				src={fallbackSrc}
				srcSet={fallbackSrcSet}
				sizes={sizesAttr}
				alt={alt}
				className={className}
				loading={loading}
				decoding={decoding}
			/>
		</picture>
	);
};

export default ResponsiveImage;
